import { zodResolver } from '@hookform/resolvers/zod';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import classnames from 'classnames';
import Button from 'components/actions/Button';
import { countries } from 'country-data-list';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useListRolesQuery } from 'services/auth';
import { useListSystemTeamsQuery } from 'services/systemAdmin/teams';
import { getUserFormData, setUserFormData } from 'store/slices/settingsPageSlice';

import FormFieldInput from './FormFieldInput';
import FormFieldMultiSelect from './FormFieldMultiSelect';
import FormFieldSelect from './FormFieldSelect';
import { createUserFormSchema, editUserFormSchema } from './schemas';
import type { UserFormData } from './types';

import type { User } from 'API';

interface UserFormProps {
    onSubmit: (data: UserFormData) => void;
    onCancel: () => void;
    isLoading: boolean;
    isExecuting: boolean;
    user: User | undefined;
}

const UserForm = ({ user, onSubmit, onCancel, isLoading, isExecuting }: UserFormProps) => {
    const userFormData = useSelector(getUserFormData);
    const dispatch = useDispatch();
    const { data: roleData, isLoading: rolesLoading } = useListRolesQuery('ListRoles');

    const { data: teamsData, isLoading: teamsLoading } = useListSystemTeamsQuery('ListSystemTeams', {
        refetchOnMountOrArgChange: true,
    });

    const countriesList: SelectOption[] = countries.all.map(value => ({
        id: value.name,
        label: value.name,
    }));

    const isValidUUID = (str: string) => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(str);
    };

    const defaultValues = useMemo<UserFormData>(
        () => ({
            id: user?.id ?? userFormData?.id,
            firstName: user?.firstName ?? userFormData?.firstName ?? '',
            lastName: user?.lastName ?? userFormData?.lastName ?? '',
            city: user?.city ?? userFormData?.city ?? '',
            country: user?.country ?? userFormData?.country ?? '',
            email: user?.email ?? userFormData?.email ?? '',
            phone: user?.phone ?? userFormData?.phone ?? '',
            roles:
                user?.roles?.map(r => r.id).filter((id): id is string => isValidUUID(id)) ?? userFormData?.roles ?? [],
            teams:
                user?.teams?.map(t => t.id).filter((id): id is string => isValidUUID(id)) ?? userFormData?.teams ?? [],
        }),
        [user, userFormData]
    );

    const schema = user ? editUserFormSchema() : createUserFormSchema();

    const {
        handleSubmit,
        formState: { isValid, errors },
        control,
        watch,
        setValue,
    } = useForm<UserFormData>({
        resolver: zodResolver(schema),
        defaultValues,
        mode: 'all',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        if (!roleData || !teamsData) {
            return;
        }

        if (!userFormData || Object.keys(userFormData).length === 0) {
            dispatch(setUserFormData(defaultValues));
            Object.entries(defaultValues).forEach(([key, value]) => setValue(key as keyof UserFormData, value));
        }
    }, [roleData, teamsData, defaultValues, dispatch, setValue, userFormData]);

    useEffect(() => {
        const subscription = watch(values => {
            dispatch(setUserFormData(values as UserFormData));
        });
        return () => subscription.unsubscribe();
    }, [watch, dispatch, defaultValues]);

    if (rolesLoading || teamsLoading) {
        return (
            <div className='UserForm__spinner'>
                <Spinner text={'Loading...'} show isFullSized />
            </div>
        );
    }

    if (!roleData || !teamsData) {
        return;
    }

    const roleOptions: SelectOption[] = roleData.map(value => ({
        id: value.id,
        label: value.name,
    }));

    const teamOptions: SelectOption[] = teamsData.map(value => ({
        id: value.id,
        label: value.name,
    }));

    return (
        <form className='UserForm__form' onSubmit={handleSubmit(onSubmit)}>
            <p className='UserForm__bodyText'>Personal</p>
            <div className='UserForm__bodyRow'>
                <div className='UserForm__bodyColumn'>
                    <FormFieldInput
                        label='First name'
                        placeholder='Insert first name'
                        required
                        name='firstName'
                        control={control}
                        error={errors.firstName}
                    />
                </div>
                <div className='UserForm__bodyColumn'>
                    <FormFieldInput
                        label='Last name'
                        placeholder='Insert last name'
                        required
                        name='lastName'
                        control={control}
                        error={errors.lastName}
                    />
                </div>
            </div>
            <div className='UserForm__bodyRow'>
                <div className='UserForm__bodyColumn'>
                    <FormFieldInput
                        label='E-mail'
                        placeholder='Insert e-mail'
                        disabled={user !== undefined}
                        required
                        name='email'
                        control={control}
                        error={errors.email}
                    />
                </div>
                <div className='UserForm__bodyColumn'>
                    <FormFieldInput
                        label='Phone number'
                        placeholder='Insert phone'
                        name='phone'
                        control={control}
                        error={errors.phone}
                    />
                </div>
            </div>
            <br />
            <p className='UserForm__bodyText'>Work location</p>
            <div className='UserForm__bodyRow'>
                <div className='UserForm__bodyColumn'>
                    <FormFieldSelect
                        control={control}
                        label='Country'
                        placeholder='Select country'
                        name='country'
                        error={errors.country}
                        options={countriesList}
                    />
                </div>
                <div className='UserForm__bodyColumn'>
                    <FormFieldInput
                        label='City'
                        placeholder='Insert city'
                        name='city'
                        control={control}
                        error={errors.city}
                    />
                </div>
            </div>
            <br />
            <p className='UserForm__bodyText'>Operational</p>
            <div className='UserForm__bodyRow'>
                <div className='UserForm__bodyColumn'>
                    <FormFieldMultiSelect
                        control={control}
                        label='Role(s)'
                        required
                        placeholder='Select roles'
                        name='roles'
                        error={Array.isArray(errors?.roles) ? errors?.roles[0] : errors?.roles}
                        options={roleOptions}
                    />
                </div>
                <div className='UserForm__bodyColumn'>
                    <FormFieldMultiSelect
                        control={control}
                        label='Team(s)'
                        placeholder='Select teams'
                        name='teams'
                        error={Array.isArray(errors?.teams) ? errors?.teams[0] : errors?.teams}
                        options={teamOptions}
                    />
                </div>
            </div>
            <hr className='UserForm__divider' />
            <div className='UserForm__buttons'>
                <Button disabled={isLoading || isExecuting} data-testid='user-dialog-cancel' onClick={onCancel}>
                    Cancel
                </Button>
                <OverlayTrigger
                    placement='top'
                    overlay={
                        <Tooltip
                            className={classnames('ButtonWithTooltip__tooltip', {
                                'ButtonWithTooltip__tooltip--hidden': !(!isValid || isLoading),
                            })}
                        >
                            <p>Complete the mandatory missing fields to continue</p>
                        </Tooltip>
                    }
                >
                    <span>
                        <Button
                            disabled={isLoading || !isValid || isExecuting}
                            type='submit'
                            data-testid='create-user-dialog-confirm'
                            style='primary'
                        >
                            {isLoading || isExecuting ? (
                                <>
                                    <span className='rioglyph rioglyph-spinner animate-spin' />
                                    {user ? 'Saving changes' : 'Creating new user'}
                                </>
                            ) : (
                                <>
                                    {user === undefined ? <span className='rioglyph rioglyph-plus' /> : <></>}
                                    {user ? 'Save changes' : 'Create new user'}
                                </>
                            )}
                        </Button>
                    </span>
                </OverlayTrigger>
            </div>
        </form>
    );
};

export default UserForm;
